import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * HorizontalScrollAnimations
 * 
 * Animates elements within the horizontal scroll section:
 * - Images: zoom in from scale 1.3 to 1.15
 * - Rotated images: start at 0deg, rotate to final rotation
 * - Connectors: draw from left to right with clip-path
 * - Captions: fade in
 * - Headings: split text character reveal
 * - Body text: fade up with subtle 20px movement
 */
class HorizontalScrollAnimations {
  constructor() {
    this.section = document.querySelector('.horizontal-scroll-section');
    if (!this.section) return;

    this.init();
  }

  /**
   * Check if we're on mobile (viewport width <= 768px)
   */
  isMobile() {
    return window.innerWidth <= 768;
  }

  /**
   * Calculate dynamic trigger offset for mobile
   * Returns progressively larger offsets as index increases
   * @param {number} index - Element index in sequence
   * @param {number} baseOffset - Base offset for desktop (e.g., 85, 75, 70)
   * @param {number} mobileBase - Starting mobile offset (e.g., 120)
   * @param {number} increment - How much to add per index (default: 5)
   */
  getTriggerStart(index, baseOffset, mobileBase = 120, increment = 5) {
    if (!this.isMobile()) {
      return `left ${baseOffset}%`;
    }
    // On mobile, add increment for each index position
    const offset = mobileBase + (index * increment);
    return `left ${offset}%`;
  }

  init() {
    // Wait for HorizontalScroll to be initialized and ScrollTrigger to be ready
    setTimeout(() => {
      // Verify the main scroll tween exists
      if (!window.horizontalScrollTween) {
        console.warn('Main horizontal scroll tween not found, retrying...');
        setTimeout(() => this.init(), 200);
        return;
      }

      console.log('✅ Horizontal scroll tween found, initializing animations');
      this.animateImages();
      this.animateConnectors();
      this.animateCaptions();
      this.animateHeadings();
      this.animateBodyText();
    }, 500);
  }

  /**
   * Animate images: zoom from 1.3 to 1.15
   * Rotated images: start at 0deg, rotate to final
   */
  animateImages() {
    // All image containers
    const imageContainers = this.section.querySelectorAll(
      '.hs-card, .hs-polaroid'
    );

    imageContainers.forEach((container, index) => {
      const img = container.querySelector('img');
      if (!img) return;

      // Skip first image (index 0) - it should be visible immediately
      if (index === 0) {
        gsap.set(container, { opacity: 1 });
        gsap.set(img, { scale: 1.15, xPercent: 0 });
        return;
      }

      // Get final rotation from container's transform
      const computedStyle = window.getComputedStyle(container);
      const transform = computedStyle.transform;
      let finalRotation = 0;

      if (transform && transform !== 'none') {
        const matrix = new DOMMatrix(transform);
        const angle = Math.atan2(matrix.b, matrix.a) * (180 / Math.PI);
        finalRotation = angle;
      }

      // Set initial states - container hidden, image zoomed in with parallax offset
      gsap.set(container, { 
        opacity: 0
      });
      
      gsap.set(img, {
        scale: 1.3,
        xPercent: -5
      });

      // If container has rotation, start it at 0
      if (Math.abs(finalRotation) > 0.1) {
        gsap.set(container, { rotation: 0 });
      }

      // Calculate trigger point - mobile needs progressively earlier triggers
      // Base: 85% desktop, 130% mobile, +12% per index on mobile
      let triggerStart = this.getTriggerStart(index, 85, 130, 12);
      
      // horizontal-5.jpg (index 4) should come in much earlier
      if (index === 4) {
        triggerStart = this.isMobile() ? 'left 200%' : 'left 110%';
      }
      // horizontal-6.jpg (index 5) should animate in between early and late timing
      else if (index === 5) {
        triggerStart = this.isMobile() ? 'left 110%' : 'left 100%';
      }
      // horizontal-7.jpg (index 6) 
      else if (index === 6) {
        triggerStart = this.isMobile() ? 'left 115%' : 'left 105%';
      }
      // horizontal-8.jpg (index 7)
      else if (index === 7) {
        triggerStart = this.isMobile() ? 'left 120%' : 'left 110%';
      }
      // horizontal-9.jpg (index 8)
      else if (index === 8) {
        triggerStart = this.isMobile() ? 'left 125%' : 'left 115%';
      }

      // Create animation timeline
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: container,
          start: triggerStart,
          horizontal: true,
          containerAnimation: window.horizontalScrollTween,
          toggleActions: 'play none none none',
          once: true
        }
      });

      // Quick fade-in container first (0.3s)
      tl.to(container, {
        opacity: 1,
        duration: 0.3,
        ease: 'power2.out'
      }, 0);

      // Then zoom out from 1.3 to 1.15 with horizontal parallax
      tl.to(img, {
        scale: 1.15,
        xPercent: 5,
        duration: 1.4,
        ease: 'power2.out'
      }, 0.15);

      // Rotate container if needed
      if (Math.abs(finalRotation) > 0.1) {
        tl.to(container, {
          rotation: finalRotation,
          duration: 1.4,
          ease: 'power2.out'
        }, 0);
      }
    });
  }

  /**
   * Animate connectors: draw from left to right with clip-path
   */
  animateConnectors() {
    const connectors = this.section.querySelectorAll('.hs-connector');

    connectors.forEach((connector, index) => {
      const svg = connector.querySelector('svg');
      if (!svg) return;

      // Skip first connector (index 0) - it should be visible immediately
      if (index === 0) {
        gsap.set(svg, { clipPath: 'inset(0 0% 0 0)' });
        return;
      }

      // Set initial clip-path
      gsap.set(svg, {
        clipPath: 'inset(0 100% 0 0)'
      });

      // Connector 2 (index 1) needs to animate earlier - before "A New Chapter" heading
      // Use earlier trigger than other connectors
      // IMPORTANT: Higher % = triggers earlier in horizontal scroll
      let connectorStart;
      if (index === 1) {
        connectorStart = this.getTriggerStart(index, 95, 140, 8); // Earlier: 95% desktop, 140% mobile (higher = earlier)
      } 
      // Connector 3 (index 2) needs to come just before horizontal-5.jpg (110%/200%)
      else if (index === 2) {
        connectorStart = this.isMobile() ? 'left 202%' : 'left 112%';
      }
      // Connector 4 (index 3) needs to come before horizontal-8.jpg (110%/120%)
      else if (index === 3) {
        connectorStart = this.isMobile() ? 'left 125%' : 'left 115%';
      }
      else {
        connectorStart = this.getTriggerStart(index, 70, 110, 8);
      }

      gsap.to(svg, {
        clipPath: 'inset(0 0% 0 0)',
        duration: 1.2,
        ease: 'power2.inOut',
        scrollTrigger: {
          trigger: connector,
          start: connectorStart,
          end: this.isMobile() ? `left ${60 + (index * 5)}%` : 'left 30%',
          horizontal: true,
          containerAnimation: window.horizontalScrollTween,
          toggleActions: 'play none none none'
        }
      });
    });
  }

  /**
   * Animate captions: fade in, then arrows fade in after
   */
  animateCaptions() {
    const captions = this.section.querySelectorAll('.hs-caption');

    captions.forEach((caption, index) => {
      const arrow = caption.querySelector('.hs-caption__arrow');
      
      // Skip first caption (index 0) - it should be visible immediately
      if (index === 0) {
        gsap.set(caption, { opacity: 1 });
        if (arrow) {
          gsap.set(arrow, { opacity: 1 });
        }
        return;
      }
      
      // Set initial states
      gsap.set(caption, { opacity: 0 });
      if (arrow) {
        gsap.set(arrow, { opacity: 0 });
      }

      // Caption fade in - progressively earlier trigger on mobile based on position
      // Special boost for later captions (4+)
      let captionStart;
      
      // Caption 3 (index 2) should sync with polaroid--vision image (image index 5)
      // Use the same trigger calculation as images
      if (index === 2) {
        captionStart = this.getTriggerStart(5, 85, 130, 12);
      }
      // Caption 4 (index 3)
      else if (index === 3) {
        captionStart = this.isMobile() ? 'left 115%' : 'left 105%';
      }
      else if (this.isMobile() && index >= 3) {
        // Caption 4+ gets massive boost: 180% + extra per index
        captionStart = `left ${180 + (index * 20)}%`;
      } else {
        captionStart = this.getTriggerStart(index, 75, 130, 15);
      }
      
      const captionTl = gsap.to(caption, {
        opacity: 1,
        duration: 0.8,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: caption,
          start: captionStart,
          horizontal: true,
          containerAnimation: window.horizontalScrollTween,
          toggleActions: 'play none none none'
        }
      });

      // Arrow fade in after caption completes (except caption 3 which comes in immediately)
      if (arrow) {
        gsap.to(arrow, {
          opacity: 1,
          duration: 0.6,
          ease: 'power2.out',
          delay: index === 2 ? 0 : 0.8, // Caption 3 arrow comes in immediately, others after caption animation completes
          scrollTrigger: {
            trigger: caption,
            start: captionStart,
            horizontal: true,
            containerAnimation: window.horizontalScrollTween,
            toggleActions: 'play none none none'
          }
        });
      }
    });
  }

  /**
   * Animate headings: split text character reveal (like hero)
   */
  animateHeadings() {
    const headings = this.section.querySelectorAll(
      '.hs-intro__heading, .hs-text__heading'
    );

    headings.forEach((heading, index) => {
      // Skip intro heading (index 0) - it should be visible immediately
      if (index === 0 && heading.classList.contains('hs-intro__heading')) {
        return;
      }
      
      // Split text into characters
      const chars = this.splitText(heading);
      
      // Separate regular chars from last-word chars
      const regularChars = [];
      const lastWordChars = [];
      
      chars.forEach(char => {
        const parent = char.parentElement.parentElement;
        if (parent && parent.classList.contains('last-word')) {
          lastWordChars.push(char);
        } else {
          regularChars.push(char);
        }
      });
      
      // Set initial state for regular chars
      if (regularChars.length > 0) {
        gsap.set(regularChars, {
          yPercent: 100,
          opacity: 0
        });
      }
      
      // Set initial state for last-word chars (opacity only to avoid clipping)
      if (lastWordChars.length > 0) {
        gsap.set(lastWordChars, {
          opacity: 0
        });
      }

      // Progressively earlier trigger on mobile for text animations based on position
      // Special boost for later text blocks (opening soon is likely index 1+)
      let headingStart;
      // "Expanding the Vision" (index 3) should come right after horizontal-5.jpg
      if (index === 3) {
        headingStart = this.isMobile() ? 'left 230%' : 'left 140%';
      }
      // "Opening Soon" (index 4) should trigger at proper viewing position
      else if (index === 4) {
        headingStart = this.isMobile() ? 'left 120%' : 'left 110%';
      }
      else if (this.isMobile() && index >= 1) {
        // Later text blocks get massive boost: 170% + extra per index
        headingStart = `left ${170 + (index * 25)}%`;
      } else {
        headingStart = this.getTriggerStart(index, 90, 110, 15);
      }
      
      // Animate regular characters with stagger
      if (regularChars.length > 0) {
        gsap.to(regularChars, {
          yPercent: 0,
          opacity: 1,
          duration: 0.8,
          stagger: 0.05,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: heading,
            start: headingStart,
            horizontal: true,
            containerAnimation: window.horizontalScrollTween,
            toggleActions: 'play none none none'
          }
        });
      }
      
      // Animate last-word characters with opacity only (no clipping)
      if (lastWordChars.length > 0) {
        gsap.to(lastWordChars, {
          opacity: 1,
          duration: 0.8,
          stagger: 0.05,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: heading,
            start: headingStart,
            horizontal: true,
            containerAnimation: window.horizontalScrollTween,
            toggleActions: 'play none none none'
          }
        });
      }
    });
  }

  /**
   * Animate body text: fade up 20px
   */
  animateBodyText() {
    const bodyTexts = this.section.querySelectorAll(
      '.hs-intro__body, .hs-text__body'
    );

    bodyTexts.forEach((text, index) => {
      // Skip intro body text (index 0) - it should be visible immediately
      if (index === 0 && text.classList.contains('hs-intro__body')) {
        gsap.set(text, { y: 0, opacity: 1 });
        return;
      }
      
      gsap.set(text, {
        y: 20,
        opacity: 0
      });

      // Progressively earlier trigger on mobile for body text based on position
      // Special boost for later text blocks to match heading timing
      let bodyStart;
      // "Expanding the Vision" body text (index 2) should match its heading timing
      if (index === 2) {
        bodyStart = this.isMobile() ? 'left 230%' : 'left 140%';
      }
      // "Opening Soon" body text (index 3) should match its heading timing
      else if (index === 3) {
        bodyStart = this.isMobile() ? 'left 120%' : 'left 110%';
      }
      else if (this.isMobile() && index >= 1) {
        // Later text blocks get massive boost: 170% + extra per index
        bodyStart = `left ${170 + (index * 25)}%`;
      } else {
        bodyStart = this.getTriggerStart(index, 90, 110, 15);
      }
      
      gsap.to(text, {
        y: 0,
        opacity: 1,
        duration: 0.8,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: text,
          start: bodyStart,
          horizontal: true,
          containerAnimation: window.horizontalScrollTween,
          toggleActions: 'play none none none',
          // Delay slightly so it happens after heading starts
          delay: 0.3
        }
      });
    });
  }

  /**
   * Split text into characters with mask structure
   * Preserves existing .last-word spans but doesn't mask them (to avoid clipping italics)
   * Groups characters by word with white-space: nowrap
   */
  splitText(element) {
    const chars = [];
    const childNodes = Array.from(element.childNodes);

    element.innerHTML = '';

    childNodes.forEach((node, nodeIndex) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent;
        const words = text.split(' ');
        
        words.forEach((word, wordIndex) => {
          if (word.length === 0) return;
          
          // Create word wrapper with nowrap
          const wordWrapper = document.createElement('span');
          wordWrapper.style.display = 'inline-block';
          wordWrapper.style.whiteSpace = 'nowrap';
          
          // Split word into characters
          for (let i = 0; i < word.length; i++) {
            const char = word[i];

            // Regular characters get mask wrapper
            const outerSpan = document.createElement('span');
            outerSpan.style.display = 'inline-block';
            outerSpan.style.overflow = 'hidden';
            outerSpan.style.verticalAlign = 'top';

            const innerSpan = document.createElement('span');
            innerSpan.style.display = 'inline-block';
            innerSpan.textContent = char;

            outerSpan.appendChild(innerSpan);
            wordWrapper.appendChild(outerSpan);
            chars.push(innerSpan);
          }
          
          element.appendChild(wordWrapper);
          
          // Check if next node is last-word span - if so, add line break instead of space
          const nextNode = childNodes[nodeIndex + 1];
          const isBeforeLastWord = nextNode && nextNode.nodeType === Node.ELEMENT_NODE && nextNode.classList.contains('last-word');
          
          // Add space or line break after word (except last word)
          if (wordIndex < words.length - 1) {
            if (isBeforeLastWord) {
              element.appendChild(document.createElement('br'));
            } else {
              element.appendChild(document.createTextNode(' '));
            }
          } else if (isBeforeLastWord) {
            element.appendChild(document.createElement('br'));
          }
        });
      } else if (node.nodeType === Node.ELEMENT_NODE && node.classList.contains('last-word')) {
        // Preserve .last-word span but DON'T add mask wrapper (no overflow:hidden)
        const lastWordSpan = document.createElement('span');
        lastWordSpan.className = 'last-word';
        lastWordSpan.style.display = 'inline-block';
        lastWordSpan.style.whiteSpace = 'nowrap';
        
        const text = node.textContent;
        for (let i = 0; i < text.length; i++) {
          const char = text[i];
          
          if (char === ' ') {
            lastWordSpan.innerHTML += ' ';
            continue;
          }

          // Last-word characters: just inline-block, no mask wrapper
          const charSpan = document.createElement('span');
          charSpan.style.display = 'inline-block';
          charSpan.textContent = char;

          lastWordSpan.appendChild(charSpan);
          chars.push(charSpan);
        }
        
        element.appendChild(lastWordSpan);
      }
    });

    return chars;
  }
}

export default HorizontalScrollAnimations;
